import moment from 'moment';

type unitOfTimeType = 'years' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds';

export const DATE_MDY = (dateObj: any): string => {
	return moment.utc(dateObj).format('MM/DD/YYYY');
};

export const DATE_TIME = (dateObj: any): string => {
	return moment(dateObj).format('MM/DD/YYYY HH:mm:ss');
};

export const DATE_YMD = (dateObj: any): string => {
	return FORMAT_DATE(dateObj, 'YYYY-MM-DD');
};

export const DATE_MDY_LOCAL = (dateObj: any): string => {
	return FORMAT_DATE(dateObj, 'MM/DD/YYYY');
};

export const FORMAT_DATE = (dateObj: any, format: string): string => {
	return moment(dateObj).format(format);
};

export const YMD_TO_MOMENT = (dateString: string): moment.Moment => {
	return moment(dateString, 'YYYY-MM-DD');
};

export const TO_MOMENT = (dateString: string, format: string): moment.Moment => {
	return moment(dateString, format);
};

export const IS_EXISTING_DATE = (dateValue: string, formats?: string | string[]) => {
	return moment(dateValue, formats ?? 'L').isValid();
};

export const IS_LESS_THAN_CURRENT_TIMESTAMP = (expiresAt: any): boolean => {
	return moment(expiresAt) < moment();
};

export const CALCULATE_TRANSIT_DAYS = (pickupDate, deliveryDate): number => {
	if (pickupDate != 'Unknown' && deliveryDate != 'Unknown') {
		return moment(deliveryDate).diff(moment(pickupDate), 'days');
	} else if (deliveryDate != null) {
		return deliveryDate;
	} else {
		return 1;
	}
};

export const TIMESTAMP_FROM_STRING = (obj: any): any => {
	return moment(obj).toISOString();
};

export const ADD_DAYS = (obj: any, daysToAdd: number): string => {
	return moment(obj).add(daysToAdd, 'days').format('MM/DD/YYYY');
};

export const YESTERDAY = (): moment.Moment => {
	return moment().subtract(1, 'days');
};

export const TODAY = (): moment.Moment => {
	return moment();
};

export const TOMORROW = (): moment.Moment => {
	return moment().add(1, 'days');
};

export const AFTER_7_DAYS = (): moment.Moment => {
	return moment().add(6, 'days');
};

export const AFTER_30_DAYS = (): moment.Moment => {
	return moment().add(29, 'days');
};

export const AFTER_90_DAYS = (): moment.Moment => {
	return moment().add(89, 'days');
};

export const END_OF_MONTH_MOMENT_OBJ = (): moment.Moment => {
	return moment().endOf('month');
};

export const START_OF_NEXT_MONTH = (): moment.Moment => {
	return moment().add(1, 'month').startOf('month');
};

export const END_OF_NEXT_MONTH = (): moment.Moment => {
	return moment().add(1, 'month').endOf('month');
};

export const GET_MOMENT = (): string => {
	return TODAY().format('MM/DD/YYYY');
};

export const START_OF_MONTH = (): string => {
	return moment().startOf('month').format('MM-DD-YYYY');
};

export const END_OF_MONTH = (): string => {
	return moment().endOf('month').format('MM-DD-YYYY');
};

export const DIFFERENCE_IN_DAYS = (startDate, endDate): number => {
	return moment(endDate).diff(moment(startDate), 'days') + 1;
};

export const IS_VALID_DATE = (dateValue: string, acceptedFormats: string[]) => {
	return moment(dateValue, acceptedFormats, true).isValid();
};

export const GET_UTC_DATE = (localDate: string) => {
	let dateValue = moment(localDate).utc().format('YYYY-MM-DD HH:mm');
	return moment.utc(dateValue).format('YYYY-MM-DD');
};

export const TO_UTC = (localDate: moment.Moment): moment.Moment => {
	return localDate.utc();
};

export const FROM_UTC = (utcDate: any): moment.Moment => {
	return moment.utc(utcDate).local();
};

export const FROM_UTC_TO_STRING = (utcDate: string, format: string = 'YYYY-MM-DD'): string => {
	return moment.utc(utcDate).format(format);
};

export const PARSE_UTC_DATE = (dateStr) => {
	if (!dateStr) return '';
	try {
		// Handle format: "Wed Aug 06 00:00:00 UTC 2025"
		// Moment formatting considers this an invalid date on the iphone.
		const parts = dateStr.replace(' UTC', '').split(' ');
		if (parts.length >= 4) {
			const monthMap = {
				Jan: '01',
				Feb: '02',
				Mar: '03',
				Apr: '04',
				May: '05',
				Jun: '06',
				Jul: '07',
				Aug: '08',
				Sep: '09',
				Oct: '10',
				Nov: '11',
				Dec: '12',
			};
			const month = monthMap[parts[1]];
			const day = parts[2].padStart(2, '0');
			const year = parts[4];
			return `${month}/${day}/${year}`;
		}
		return moment(dateStr).format('MM/DD/YYYY');
	} catch (error) {
		return '';
	}
};

export const ENUMERATE_DAYS_BETWEEN_DATES = (startDate: any, endDate: any): string => {
	startDate = moment(startDate);
	endDate = moment(endDate);
	let now = startDate,
		dates = [];
	while (now.isBefore(endDate) || now.isSame(endDate)) {
		dates.push(now.format('YYYY-MM-DD'));
		now.add(1, 'days');
	}
	return dates.join(', ');
};

export const GET_UNIX_TIMESTAMP = (dateobj: any): any => {
	return moment(dateobj).unix();
};

export const SUBTRACT_DAYS_FROM_TODAY = (daysToSubtract: number): moment.Moment => {
	return moment().subtract(daysToSubtract, 'days');
};

export const SUBTRACT_FROM_TODAY = (
	durationToSubtract: number,
	unitOfTime: unitOfTimeType
): moment.Moment => {
	return moment().subtract(durationToSubtract, unitOfTime);
};

export const SET_TIME_TO_START_OF_THE_DAY = (date: any): moment.Moment => {
	return moment(date).startOf('day');
};

export const SET_TIME_TO_END_OF_THE_DAY = (date: any): moment.Moment => {
	return moment(date).endOf('day');
};

export const DIFFERENCE_FROM_NOW = (dateInUtc: Date, unitOfTime: unitOfTimeType): number => {
	return moment().diff(moment(FROM_UTC(dateInUtc)), unitOfTime);
};

export const DIFFERENCE_IN_SECONDS = (dateToCompare: Date | string | moment.Moment): number => {
	return moment().diff(dateToCompare, 'seconds');
};

export const GET_FORMATTED_DATE = (dateInput: string | Date): string => {
	const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
	if (!(date instanceof Date) || isNaN(date.getTime())) {
		return '';
	}

	const day = date.getDate();
	const suffix = GET_ORDINAL_SUFFIX(day);
	const month = date.toLocaleString('en-US', { month: 'long' });
	const year = date.getFullYear();

	return `${month} ${day}${suffix} ${year}`;
};

export const GET_ORDINAL_SUFFIX = (day: number): string => {
	if (day > 3 && day < 21) return 'th';
	switch (day % 10) {
		case 1:
			return 'st';
		case 2:
			return 'nd';
		case 3:
			return 'rd';
		default:
			return 'th';
	}
};

// get date from string, input ex: 20250602080000
export const GET_DATE_FROM_STRING = (dateStr: string): Date | null => {
	if (!dateStr || dateStr.length < 8) return null;

	const year = +dateStr.slice(0, 4);
	const month = +dateStr.slice(4, 6) - 1;
	const day = +dateStr.slice(6, 8);

	if (isNaN(year) || isNaN(month) || isNaN(day) || month < 0 || month > 11 || day < 1 || day > 31) {
		return null;
	}

	return new Date(year, month, day);
};

// input ex: 20250602080000, 20250602150000
export const GET_TIME_FROM_STRING = (dateStr: string): string => {
	if (!dateStr || dateStr.length < 14) return null;
	const hours = +dateStr.slice(8, 10);
	const minutes = +dateStr.slice(10, 12);
	const seconds = +dateStr.slice(12, 14);
	if (
		isNaN(hours) ||
		isNaN(minutes) ||
		isNaN(seconds) ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59
	) {
		return '';
	}

	return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
